import { AIMessageContext } from '@diagon-alley/common';
import useAIChatRenderContext from '@hooks/ai/useAIChatRenderContext';
import { Box, Stack, Transition } from '@mantine/core';
import useAiAnalystContext from '@providers/AiAnalyst/useAiAnalystContext';
import { useMemo } from 'react';
import ChatInputForm from './ChatInputForm';
import ChatWithMessages from './ChatWithMessages';
import EmptyChatBody from './EmptyChatBody';

const ThinkingMessage = ({
    label,
    withDelay,
}: {
    label: string;
    withDelay?: boolean;
}) => {
    return (
        <Box className="flex items-center justify-center p-4">
            <Box className="text-sm text-gray-500">{label}</Box>
        </Box>
    );
};

const AnalystChatBody: React.FC<{
    isCompact?: boolean;
    emptyStateTitle?: string;
    emptyStateDescription?: string;
    showSuggestions?: boolean;
}> = ({
    isCompact = false,
    emptyStateTitle,
    emptyStateDescription,
    showSuggestions = true,
}) => {
    const { aiChatMessages, isLoadingActiveThreadMessages } =
        useAiAnalystContext((context) => context.state);

    const { getCurrentContext: currentChatContext } = useAIChatRenderContext();

    const isRenderedInHomePage = useMemo(() => {
        return currentChatContext === AIMessageContext.HOME_PAGE;
    }, [currentChatContext]);

    // Layout configuration based on mode
    const layoutConfig = useMemo(() => {
        if (isCompact) {
            return {
                containerClass: 'relative flex min-h-0 w-full flex-1 flex-col',
                contentStackClass:
                    'flex h-full min-h-0 w-full flex-1 flex-col gap-3 overflow-y-auto px-4 pt-4',
                // Remove border and extra padding for a streamlined look
                inputWrapperClass: 'bg-white p-3',
                inputInnerClass: 'w-full',
                emptyPaddingTop: '2vh',
                showInputShadow: false,
            };
        }
        return {
            containerClass:
                'relative flex min-h-0 w-full flex-1 overflow-hidden px-3 pb-32',
            contentStackClass:
                'mx-auto flex h-full min-h-0 w-full max-w-5xl flex-1 flex-col gap-4 pt-6',
            inputWrapperClass:
                'pointer-events-none absolute inset-x-0 bottom-0 z-40 flex justify-center px-3 pb-3',
            inputInnerClass:
                'pointer-events-auto w-full max-w-5xl rounded-2xl border border-gray-100 bg-white/95 pt-2 pb-3 shadow-[0_-6px_30px_rgba(0,0,0,0.06)] backdrop-blur supports-[backdrop-filter]:backdrop-blur-md px-3',
            emptyPaddingTop: isRenderedInHomePage ? '10vh' : '4vh',
            showInputShadow: true,
        };
    }, [isCompact, isRenderedInHomePage]);

    return (
        <Stack className={layoutConfig.containerClass}>
            <Stack className={layoutConfig.contentStackClass}>
                <Transition
                    mounted={isLoadingActiveThreadMessages}
                    transition="fade"
                    duration={200}
                    timingFunction="ease-out"
                >
                    {(styles) => (
                        <Box className="flex-1" style={styles}>
                            <ThinkingMessage
                                label="Loading chat..."
                                withDelay={false}
                            />
                        </Box>
                    )}
                </Transition>

                <Transition
                    mounted={!isLoadingActiveThreadMessages}
                    transition="fade"
                    duration={200}
                    timingFunction="ease-out"
                >
                    {(styles) => (
                        <Stack
                            className="min-h-0 w-full flex-1 gap-2"
                            style={styles}
                        >
                            {aiChatMessages.length > 0 ? (
                                <Box className="flex min-h-0 w-full flex-1 flex-col">
                                    <Box className="flex min-h-0 flex-1">
                                        <ChatWithMessages />
                                    </Box>
                                </Box>
                            ) : (
                                <Stack
                                    className="w-full"
                                    style={{
                                        paddingTop:
                                            layoutConfig.emptyPaddingTop,
                                    }}
                                >
                                    <EmptyChatBody
                                        title={emptyStateTitle}
                                        description={emptyStateDescription}
                                        showSuggestions={showSuggestions}
                                    />
                                </Stack>
                            )}
                        </Stack>
                    )}
                </Transition>
            </Stack>

            <Box className={layoutConfig.inputWrapperClass}>
                <Box className={layoutConfig.inputInnerClass}>
                    <ChatInputForm isCompact={isCompact} />
                </Box>
            </Box>
        </Stack>
    );
};

export default AnalystChatBody;
